import type { DateType } from '@date-io/type'
import { IconButton } from '@patrianna/core-components'
import clsx from 'classnames'
import * as PropTypes from 'prop-types'
import * as React from 'react'

import { useUtils } from '../../_shared/hooks/useUtils'
import { ArrowLeftIcon } from '../../_shared/icons/ArrowLeftIcon'
import { ArrowRightIcon } from '../../_shared/icons/ArrowRightIcon'
import type { MaterialUiPickersDate } from '../../typings/date'

import SlideTransition from './SlideTransition'
import type { SlideDirection } from './SlideTransition'

export interface CalendarHeaderProps {
  currentMonth: DateType
  leftArrowIcon?: React.ReactNode
  rightArrowIcon?: React.ReactNode
  leftArrowButtonProps?: any
  rightArrowButtonProps?: any
  disablePrevMonth?: boolean
  disableNextMonth?: boolean
  slideDirection: SlideDirection
  onMonthChange: (date: MaterialUiPickersDate, direction: SlideDirection) => void | Promise<void>
}

export const CalendarHeader: React.FunctionComponent<CalendarHeaderProps> = ({
  currentMonth,
  onMonthChange,
  leftArrowIcon = <ArrowLeftIcon />,
  rightArrowIcon = <ArrowRightIcon />,
  leftArrowButtonProps,
  rightArrowButtonProps,
  disablePrevMonth = false,
  disableNextMonth = false,
  slideDirection,
}) => {
  const utils = useUtils()
  const rtl = false

  const selectNextMonth = () => onMonthChange(utils.getNextMonth(currentMonth), 'left')
  const selectPreviousMonth = () => onMonthChange(utils.getPreviousMonth(currentMonth), 'right')

  return (
    <div>
      <div className={'MuiPickersCalendarHeader-switchHeader'}>
        <IconButton
          {...leftArrowButtonProps}
          disabled={disablePrevMonth}
          onClick={selectPreviousMonth}
          className={'MuiPickersCalendarHeader-iconButton'}
          data-test='datepicker-arrow-left'
        >
          {rtl ? rightArrowIcon : leftArrowIcon}
        </IconButton>

        <SlideTransition
          slideDirection={slideDirection}
          transKey={currentMonth.toString()}
          className={'MuiPickersCalendarHeader-transitionContainer'}
        >
          <span data-test='datepicker-current-month' className='mt-typography-body1 mt-typography-alignCenter'>
            {utils.getCalendarHeaderText(currentMonth)}
          </span>
        </SlideTransition>

        <IconButton
          {...rightArrowButtonProps}
          disabled={disableNextMonth}
          onClick={selectNextMonth}
          className={'MuiPickersCalendarHeader-iconButton'}
          data-test='datepicker-arrow-right'
        >
          {rtl ? leftArrowIcon : rightArrowIcon}
        </IconButton>
      </div>

      <div className={'MuiPickersCalendarHeader-daysHeader'}>
        {utils.getWeekdays().map((day, index) => (
          <span key={index} className={clsx('mt-typography-caption MuiPickersCalendarHeader-dayLabel')}>
            {day}
          </span>
        ))}
      </div>
    </div>
  )
}

CalendarHeader.displayName = 'CalendarHeader'

CalendarHeader.propTypes = {
  leftArrowIcon: PropTypes.element,
  rightArrowIcon: PropTypes.element,
  disablePrevMonth: PropTypes.bool,
  disableNextMonth: PropTypes.bool,
}

export default CalendarHeader
